import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState } from "react";
import type { Me } from "@alm/api-contracts";
import { portalHomeFor } from "@alm/domain";
import { orpc } from "@/lib/orpc";
import { panelExtensionId, tellPanel } from "@/routes/_authenticated/nancy/-lib/panel-courier";
import { PanelGround } from "./-components/panel-ground";
import { Spinner } from "@/components/ui/spinner";
import { PanelSignedOut } from "./-components/panel-signed-out";

// Its own chunk: the signed-out panel stays light, and the chat comes only with a session.
const PanelSignedIn = lazy(() =>
  import("./-components/panel-signed-in").then((module) => ({ default: module.PanelSignedIn })),
);

// Nancy in the extension's side panel, outside _authenticated: no shell fits 360 pixels and sign-in
// happens in a tab. Same API, procedures and session as the website: two views of one order.
// Only this app and Nancy's extension may frame it (lib/workspace/panel-framing.ts).
export const Route = createFileRoute("/panel")({
  ssr: false,
  beforeLoad: async ({ context }): Promise<{ me: Me | null }> => {
    try {
      // Asked once: a signed-out answer retried three times held the panel on a spinner.
      return {
        me: await context.queryClient.ensureQueryData({
          ...orpc.auth.me.queryOptions(),
          retry: false,
        }),
      };
    } catch {
      return { me: null };
    }
  },
  head: () => ({ meta: [{ title: "Nancy | People AI" }] }),
  component: PanelRealm,
});

function PanelRealm() {
  const { me } = Route.useRouteContext();
  const [extensionId] = useState(() => panelExtensionId());
  // The frame around this page shows a spinner until it hears this.
  useEffect(() => {
    if (extensionId) tellPanel(extensionId, "ready");
  }, [extensionId]);

  const finishFirst = Boolean(me && (me.user.mustResetPassword || portalHomeFor(me.user.role)));
  return (
    <PanelGround>
      {!me || finishFirst ? (
        <PanelSignedOut finishFirst={finishFirst} />
      ) : (
        <Suspense
          fallback={
            <div className="flex flex-1 items-center justify-center">
              <Spinner className="text-muted-foreground size-5" />
            </div>
          }
        >
          <PanelSignedIn extensionId={extensionId} />
        </Suspense>
      )}
    </PanelGround>
  );
}
